/**
 * MRT 테이블 템플릿
 * 1. 테이블에 표시할 데이터를 설정합니다. (컬럼 정의, 데이터 정의)
 * 2. 사용자 정의 테이블 옵션을 설정합니다.
 * 3. 사용자 정의 테이블 스타일을 설정합니다.
 */

import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useMemo,
} from "react";

import {
  MaterialReactTable,
  useMaterialReactTable, // MRT Hook
} from "material-react-table";
import { MRT_Localization_KO } from "../../constants/localiztion";

import {
  Box,
  Button,
  Collapse,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import OutlinedInput from "@mui/material/OutlinedInput";

import Buttons from "../../../common/Buttons";
import { COLORS, FONT_PROPS } from "../../styles/colors";

// 테이블 템플릿 컴포넌트(새로운 버전)
// 부모 컴포넌트에서 커스텀으로 할당하여 사용 가능한 구조
export const NewTableTemplate = forwardRef(
  (
    {
      columns = [], // 컬럼 정의
      data = [], // 데이터 정의
      onSelectedRowsChange = null, // 선택된 행의 처리 함수 (부모의 리렌더링을 위한 용도)
      onRowOrdering = null, // 행(Row) 이동 처리 함수
      topToolbarContent = null, // 상단 툴바 사용자 정의 컨텐츠
      bottomToolbarContent = null, // 하단 툴바 사용자 정의 컨텐츠
      renderRowActionContent = null, // 행(row) 수정,저장 등 사용자 정의 컨텐츠
      renderRowActionHeader = null, // 행(row) 헤더의 수정,저장 등 사용자 정의 헤더
      renderRowActionCustomOptions = null, // 커스텀으로 된 행(row) 옵션을 편집하는 용도
      createDisplayMode = "modal" || "table" || "cell" || "row", // 생성 모드 표시 설정
      editDisplayMode = "modal" || "table" || "cell" || "row", // 수정 모드 표시 설정
      enableEditing = true, // 행(row)의 편집 활성화 여부
      enableTopToolbar = true, // 상단 툴바 활성화 여부
      enableBottomToolbar = true, // 하단 툴바 활성화 여부
      enableCustomGlobalFilter = false, // 전체 검색 활성화 여부
      enableColumnOrdering = false, // 열(columns) 이동 활성화 여부
      enableRowOrdering = false, // 행(row) 이동 활성화 여부
      enableRowVirtualization = true,
      enabledSelectionRow = true, // 행(row) 선택 활성화 여부
      enabledSelectionAllRow = true, // 행(row) 전체 선택 활성화 여부
      rowActionsColumnSize = 250,
      tableConainerSX = {},
      templateSX = {},
      tableMeta = {},
    },
    ref
  ) => {
    const theme = useTheme(); // MUI 테마 사용

    // 검색 필터 상태 관리
    const [globalFilter, setGlobalFilter] = useState("");

    // 전체 선택 토글 핸들러
    const handleSelectAllToggle = (checked, table) => {
      if (checked) {
        const all = {};
        table.getRowModel().rows.forEach((row) => {
          all[row.id] = true;
        });
        setSelectedRowIds(all);
      } else {
        setSelectedRowIds({});
      }
    };

    // 범위 선택 상태 관리
    const [selectionLock, setSelectionLock] = useState(false);
    const skipNextClickRef = useRef(false); // mousedown에서 이미 처리했으면 click 무시

    useEffect(() => {
      if (!selectionLock) return;

      // 혹시 남아있던 브라우저 텍스트 선택 제거
      window.getSelection?.().removeAllRanges?.();

      const onMouseUp = () => {
        setSelectionLock(false);
        window.getSelection?.().removeAllRanges?.();
        // mousedown에서 처리하고 click이 바로 이어질 때 한 번만 무시
        skipNextClickRef.current = true;
        // 다음 틱에서 자동 해제
        setTimeout(() => (skipNextClickRef.current = false), 0);
      };

      document.addEventListener("mouseup", onMouseUp, { once: true });
      return () => document.removeEventListener("mouseup", onMouseUp);
    }, [selectionLock]);

    // 키보드와 마우스로 사용하는 Selection 기능
    const [selectedRowIds, setSelectedRowIds] = useState({});
    const [lastClickedIndex, setLastClickedIndex] = useState(null);
    const handleRowClick = ({ event, row }) => {
      const visibleRows = table.getRowModel().rows; // 필터링된 후의 rows
      const index = visibleRows.findIndex((r) => r.id === row.id); // row.index 대신 안전하게 찾기
      const rowId = row.id;

      if (event.shiftKey && lastClickedIndex !== null) {
        // Shift 범위 선택
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);
        const newSelected = {};
        for (let i = start; i <= end; i++) {
          newSelected[visibleRows[i].id] = true;
        }
        setSelectedRowIds(newSelected);
      } else if (event.ctrlKey || event.metaKey) {
        // Ctrl/Cmd 토글
        setSelectedRowIds((prev) => ({
          ...prev,
          [rowId]: !prev[rowId],
        }));
        setLastClickedIndex(index);
      } else {
        // 단일 선택
        setSelectedRowIds({ [rowId]: true });
        setLastClickedIndex(index);
      }
    };
    // const handleRowClickBugVersion = ({ event, row }) => {
    //   const index = row.index;
    //   const rowId = row.id;
    //   const allRows = table.getRowModel().rows;

    //   if (event.shiftKey && lastClickedIndex !== null) {
    //     // 🔶 Shift 범위 선택
    //     const start = Math.min(lastClickedIndex, index);
    //     const end = Math.max(lastClickedIndex, index);
    //     const newSelected = {};
    //     for (let i = start; i <= end; i++) {
    //       newSelected[allRows[i].id] = true;
    //     }
    //     setSelectedRowIds(newSelected);
    //   } else if (event.ctrlKey || event.metaKey) {
    //     // 🔷 Ctrl (또는 Mac의 Cmd) → 개별 토글
    //     setSelectedRowIds((prev) => ({
    //       ...prev,
    //       [rowId]: !prev[rowId],
    //     }));
    //     setLastClickedIndex(index);
    //   } else {
    //     // ✅ 그냥 클릭 → 이미 선택된 row면 해제, 아니면 단일 선택
    //     // setSelectedRowIds((prev) => {
    //     //   if (prev[rowId]) {
    //     //     setLastClickedIndex(null);
    //     //     return {}; // 선택 해제
    //     //   } else {
    //     //     setLastClickedIndex(index);
    //     //     return { [rowId]: true }; // 새로 선택
    //     //   }
    //     // });

    //     // ✅ 그냥 클릭 → 해당 row만 단일 선택
    //     setSelectedRowIds({ [rowId]: true });
    //     setLastClickedIndex(index);
    //   }
    // };

    // 데이터가 변경되면 행 선택 초기화
    useEffect(() => {
      setSelectedRowIds({});
      setLastClickedIndex(null);
      table.setRowSelection({}); // MRT의 선택 상태 초기화

      // // setTimeout으로 다음 렌더링 이후 스크롤
      // setTimeout(() => {
      //   const lastIndex = data.length - 1;
      //   console.log('Scrolling to index:', lastIndex);
      //   rowVirtualizerRef.current?.scrollToIndex?.(lastIndex);
      // }, 0);
    }, [data]);

    // 행 선택이 변경되면 업데이트
    useEffect(() => {
      if (onSelectedRowsChange) {
        onSelectedRowsChange(selectedRowIds); // 부모에 전달
      }
    }, [selectedRowIds]);

    //////////////////////////////
    // MRT의 table 인스턴싱 (테이블 상태와 동작을 제어가 필요하여 사용중입니다.)
    //////////////////////////////
    const table = useMaterialReactTable({
      //////////////////////////////////
      // 테이블에 표시할 데이터
      //////////////////////////////////

      columns: columns, // 칼럼 정의 데이터
      data: data, // 테이블에 표시할 데이터
      //////////////////////////////////
      // Internal 테이블 옵션
      //////////////////////////////////
      enableSorting: true, // 정렬 기능
      enableColumnFilters: false, //  컬럼별 필터 기능
      enableRowSelection: false, // 행 선택 기능
      //enableSelectAll: false, // 테이블의 행 전체 선택 기능
      enablePagination: false, // 페이지네이션 기능
      enableMultiRowSelection: false, // 다중 행 선택 기능
      enableGlobalFilter: enableCustomGlobalFilter, // 전체 검색 기능
      enableDensityToggle: false, // 컨테이너 밀도 조절 기능
      enableHiding: false, // 컬럼 숨김 기능
      enableFullScreenToggle: false, // 전체 화면 기능
      enableColumnActions: false, // 컬럼 액션 기능
      enableEditing: enableEditing, // 테이블 각 행 편집 기능
      enableStickyHeader: true, // 테이블 헤더 고정 기능
      createDisplayMode: createDisplayMode, // 생성 모드 표시 설정
      editDisplayMode: editDisplayMode, // 수정 모드 표시 설정
      localization: MRT_Localization_KO, // 테이블 UI 언어 설정
      enableTopToolbar: enableTopToolbar, // 테이블 상단 툴바 활성화화 여부
      enableBottomToolbar: enableBottomToolbar, // 테이블 하단 툴바 활성화화 여부
      enableColumnOrdering: enableColumnOrdering, // columns 이동 기능
      enableRowOrdering: enableRowOrdering, // row 이동 기능
      enableRowActions: Boolean(renderRowActionContent),

      // 테이블의 현재 상태(예: 선택된 행, 정렬 상태, 필터 상태 등)를 저장하고 관리하는 객체
      state: {
        rowSelection: selectedRowIds, // 외부에서 상태 제어
        globalFilter, // ← 이 줄 추가 (전역 필터 상태를 같이 제어)
      },

      onGlobalFilterChange: setGlobalFilter, // ← 이 줄 추가
      meta: tableMeta,

      enableRowVirtualization, // 행 가상화 기능 활성화
      //  rowVirtualizerInstanceRef: rowVirtualizerRef, // 가상화 인스턴스 참조

      //////////////////////////////////

      // tbody에 스타일 적용
      muiTableBodyProps: {
        sx: {
          // 데이터가 없을 때 배경색 변경
          backgroundColor:
            data.length === 0
              ? theme.palette.background.tableEmptyBg
              : theme.palette.background.paper,
        },
      },

      // row id 재정의 (selection 기능을 위해 필요)
      getRowId: (row, index) => {
        return row.id ?? index; // (이 부분에서 현재는 사용가능하지만 페이지 네이션을 사용할 때 useffect로 데이터의 고유 id값을 매번 부여하도록 변경해야 사용 가능 할듯 하다.)
      },
      // 테이블의 각 행(Row)에 추가적인 스타일 및 이벤트 적용 (선택 기능 포함)
      muiTableBodyRowProps: ({ row }) => ({
        onDragStart: (e) => {
          if (selectionLock) e.preventDefault(); // 제스처 동안 드래그 시작도 차단
        },

        onMouseDown: (event) => {
          if (!enabledSelectionRow) return;
          if (event.button !== 0) return; // 좌클릭만

          const isShift = event.shiftKey;
          const isCtrlMeta = event.ctrlKey || event.metaKey;

          if (isShift || isCtrlMeta) {
            event.preventDefault(); // ✅ 브라우저 텍스트 선택 시작 방지
            window.getSelection?.().removeAllRanges?.(); // 잔여 파란 선택 제거
            setSelectionLock(true); // 컨테이너 user-select: none 적용

            // 네가 만든 선택 로직을 즉시 실행 (범위/토글 등 기존 handleRowClick 재사용)
            handleRowClick({ event, row });
            return; // click 단계에서 중복 실행 방지
          }
          // 일반 단일 클릭은 그대로 두고 onClick에서 처리
        },
        onClick: (event) => {
          if (!enabledSelectionRow) return;
          if (skipNextClickRef.current) return; // mousedown에서 이미 처리함
          handleRowClick({ event, row });
        },
        //selected: selectedRowIds[row.id] || false, // 선택된 행 여부
        sx: {
          cursor: enabledSelectionRow ? "pointer" : "default",
          minHeight: "50px",
          "& td": {
            backgroundColor: row.original.__bgColor
              ? row.original.__bgColor
              : theme.palette.background.paper,
            height: "auto",
            verticalAlign: "middle",
          },
          "&:hover": {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
        },
      }),

      // 상단 툴바 렌더링
      renderTopToolbar: ({ table }) => {
        // 전체 선택을 포함한 렌더링
        const includeSelectedAllRowRender = () => (
          <Box
            display="flex"
            alignItems="center"
            bgcolor={theme.palette.background.paper}
            borderBottom={`1px solid ${theme.palette.divider}`}
          >
            {/* 전체 선택 */}
            <Box
              sx={{
                width: "130px",
                display: "flex",
                alignItems: "center",
                gap: 2,
                paddingLeft: 2,
              }}
            >
              <Checkbox
                checked={
                  table.getRowModel().rows.length > 0 &&
                  table
                    .getRowModel()
                    .rows.every((row) => selectedRowIds[row.id])
                }
                indeterminate={
                  Object.keys(selectedRowIds).length > 0 &&
                  !table
                    .getRowModel()
                    .rows.every((row) => selectedRowIds[row.id])
                }
                onChange={(e) => handleSelectAllToggle(e.target.checked, table)}
              />
              <Typography
                sx={{
                  fontWeight: FONT_PROPS.SEMIBOLD,
                  fontSize: 15,
                  color: theme.text,
                }}
              >
                전체 선택
              </Typography>
            </Box>
            {/* 커스텀 topbar */}
            {topToolbarContent && (
              <Box
                sx={{
                  width: "calc(100% - 130px)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {topToolbarContent(table)}
              </Box>
            )}
          </Box>
        );
        // 전체 선택을 포함하지 않은 렌더링
        const excludeSelectedAllRowRender = () => (
          <>
            {topToolbarContent && (
              <Box
                display="flex"
                alignItems="center"
                bgcolor={theme.palette.background.paper}
                borderBottom={`1px solid ${theme.palette.divider}`}
              >
                {topToolbarContent(table)}
              </Box>
            )}
          </>
        );
        return (
          <>
            {" "}
            {enabledSelectionAllRow
              ? includeSelectedAllRowRender()
              : excludeSelectedAllRowRender()}{" "}
          </>
        );
      },

      // 하단 툴바 렌더링
      renderBottomToolbar: ({ table }) => {
        return bottomToolbarContent ? bottomToolbarContent(table) : null;
      },

      // 테이블의 각 행(Row)의 사용자 정의 액션 버튼 추가 (수정, 삭제 등)
      renderRowActions: ({ table, row }) => {
        return renderRowActionContent ? (
          <Box
            sx={{
              width: "auto !important",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            {renderRowActionContent(row.original)}
          </Box>
        ) : null;
      },
      // 테이블의 행(Row) header에 사용자 정의 액션 버튼 추가 (수정, 삭제 등)
      displayColumnDefOptions: {
        "mrt-row-actions": {
          ...(renderRowActionHeader && {
            header: renderRowActionHeader, // ✅ header 커스텀을 부모에서 넘겨줌
          }),
          size: rowActionsColumnSize, // 액션 컬럼 너비 설정
          minSize: rowActionsColumnSize, // 최소 너비
          maxSize: rowActionsColumnSize, // 최대 너비
        },
      },

      // 사용자 정의 액션 버튼 위치 설정 (첫번째 열 또는 마지막 열)
      positionActionsColumn: "last",
      // // 행(Row)을 생성할 때 나타나는 다이얼로그(Dialog) 창의 내용을 커스터마이징하는 옵션
      // renderCreateRowDialogContent: ({ table }) => {
      //   return <CreateRowModal table={table} />; // 사용자 정의 생성 모달 반환
      // },
      // // 행(Row)을 편집할 때 나타나는 다이얼로그(Dialog) 창의 내용을 커스터마이징하는 옵션
      // renderEditRowDialogContent: ({ row, table }) => {
      //   return <EditRowModal row={row} table={table} />; // 사용자 정의 편집 모달 반환
      // },

      // 행(Row) 이동 옵션
      muiRowDragHandleProps: ({ table }) => ({
        onDragEnd: () => {
          const { draggingRow, hoveredRow } = table.getState();
          if (hoveredRow && draggingRow) {
            const allRows = table.getRowModel().rows;

            const draggedRowData = allRows[draggingRow.index]?.original;
            const hoveredRowData = allRows[hoveredRow.index]?.original;
            // console.log('드래그한 row의 데이터:', draggedRowData);
            // console.log('호버된 row의 데이터:', hoveredRowData);

            onRowOrdering(draggedRowData, hoveredRowData);
          }
        },
      }),

      //////////////////////////////////
      // Internal 테이블 스타일
      //////////////////////////////////

      // 테이블을 감싼 컨테이너 스타일
      muiTableContainerProps: {
        sx: {
          flex: 1,
          height: "100%",
          overflowY: "auto",
          backgroundColor: theme.palette.background.paper,
          ...tableConainerSX, // 사용자 정의 컨테이너 스타일
        },
      },
      // 테이블을 감싼 페이퍼 스타일
      muiTablePaperProps: {
        sx: {
          boxShadow: "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },

      // 테이블 상단 툴바 스타일
      muiTopToolbarProps: {
        sx: {
          //   alignItems: "center",
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          //   p: "10px",
          //   transition: "none", // transition 제거
          //   WebkitTransition: "none", // webkit-transition 제거
          "& > div:nth-of-type(2)": {
            alignItems: "center",
            // padding: "0 !important",
          },
        },
      },

      // 테이블 하단 툴바 스타일
      muiBottomToolbarProps: {
        sx: {
          backgroundColor: theme.palette.background.paper,
          transition: "none", // transition 제거
          WebkitTransition: "none", // webkit-transition 제거
          // 하단 툴바의 특정 부분 숨기기 (이 부분을 사용하면 페이지 네이션을 사용할 수 없음.)
          "& > .MuiBox-root:nth-of-type(2)": {
            display: "none", // 두 번째 Box 요소 숨기기
          },
          // 또는 더 구체적으로 지정 (이 부분을 사용하면 페이지 네이션을 사용할 수 없음.)
          "& > .css-1llu0od, & > div:has(> span + .MuiBox-root)": {
            display: "none",
          },
        },
      },

      // 테이블 안 헤더의 전체 셀 스타일 (배경색, 높이 조정, 클릭 이벤트, 마우스 오버 효과 등)
      muiTableHeadRowProps: {
        sx: {
          backgroundColor: theme.palette.background.paper,
        },
      },
      // 테이블 안 헤더의 개별 셀 스타일 (정렬, 패딩, 텍스트색상, 툴팁, 이벤트 등)
      muiTableHeadCellProps: {
        sx: {
          paddingTop: "0.6rem",
          paddingBottom: "0.6rem",
          paddingLeft: "0.6rem",
          paddingRight: "0.1rem",
          textAlign: "center", // left, center, right
          verticalAlign: "middle", // top, middle, bottom
          fontFamily: `${FONT_PROPS.PRIMARY}`,
          color: `${FONT_PROPS.DARK}`,
          fontWeight: `${FONT_PROPS.SEMIBOLD}`,
          fontSize: 14,
        },
      },
      // 테이블 안 바디의 개별 셀 스타일 (정렬, 패딩, 텍스트색상, 툴팁, 이벤트 등)
      muiTableBodyCellProps: {
        sx: {
          color: theme.palette.text.primary,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 14,
          py: 0,
          // py: '4px',
        },
      },
      // 툴바(Top Toolbar) 상단 또는 하단에 경고 배너(Alert Banner) 스타일
      muiToolbarAlertBannerProps: {
        onClick: () => {
          setSelectedRowIds({}); // 선택된 행 ID 초기화
          setLastClickedIndex(null); // 마지막 클릭 인덱스 초기화
        },
        sx: {
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          "& .MuiAlert-message": {
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.875rem",
          },
          "& .MuiAlert-icon": {
            color: theme.palette.text.primary,
          },
          "& .MuiAlert-action": {
            color: theme.palette.text.primary,
          },
          padding: "8px 16px",
          margin: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
        },
      },
      // 경고 배너(Alert Banner) 위치 설정 (top, bottom, none)
      positionToolbarAlertBanner: "bottom",

      //////////////////////////////////
      // 사용자 정의(Custom) 테이블 옵션
      //////////////////////////////////

      // 테이블의 각 행(Row) 커스텀 옵션 편집 시 쓰는 함수 정의 (table.options?.테이블옵션 < 이렇게 사용합니다.)
      onCustomOptionsEditRow: ({ values, row, table }) => {
        if (editDisplayMode === "row") {
          renderRowActionCustomOptions?.({
            values: values,
            row: row,
            table: table,
          }); // 외부에서 편집 로직 실행
          //renderRowActionCustomOptions?.(values, row, table); // 외부에서 편집 로직 실행
        }
      },

      //////////////////////////////////
      // 사용자 정의(Custom) 테이블 스타일
      //////////////////////////////////

      // TODO:
    });

    // 외부에서 테이블의 기능을 사용할 수 있도록 ref를 통해 메서드 노출
    useImperativeHandle(ref, () => ({
      resetFilters: () => table.setColumnFilters([]),
      getSelectedRows: () => table.getSelectedRowModel().rows,

      clearSelection: () => {
        // 선택 초기화
        setSelectedRowIds({});
        setLastClickedIndex(null);
        table.setRowSelection({}); // MRT의 선택 상태 초기화
      },
    }));

    // useEffect(() => {
    //   // 컬럼 ID 순서 로그
    //   console.log('columnOrder state:', table.getState().columnOrder);
    // }, [table]);

    // MRT 테이블 렌더링
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          ...templateSX,
        }}
      >
        <MaterialReactTable table={table} />
      </Box>
    );
  }
);

/////////////////////////////////////////////

// // 테이블 템플릿 컴포넌트(레거시) - 참고용도로 남겨둠
// export function TableTemplate({
//   columns = [], // 컬럼 정의
//   data = [], // 데이터 정의
//   CreateRowModal = null, // 생성 모달 컴포넌트
//   EditRowModal = null, // 수정 모달 컴포넌트
//   ButtonRowActions = null, // 행(row) 액션 버튼 컴포넌트
//   onDeleteRows = null, // 행(row) 삭제 함수
//   onEditRow = null, // 행(row) 편집 함수
//   onSelectedRows = null, // 선택된 행의 데이터 처리 함수 (모달의 테이블에 사용합니다.)
//   onRowOrdering = null, // 행(Row) 이동 처리 함수
//   toolBarCustomButtons = null, // 상단 툴바 사용자 정의 버튼
//   createDisplayMode = "modal" || "table" || "cell" || "row", // 생성 모드 표시 설정
//   editDisplayMode = "modal" || "table" || "cell" || "row", // 수정 모드 표시 설정
//   enableEditing = true, // 행(row)의 편집 활성화 여부
//   enableTopToolbar = true, // 상단 툴바 활성화 여부
//   enableBottomToolbar = true, // 하단 툴바 활성화 여부
//   enableCustomTopToolbarActions = true, // 사용자 정의 상단 툴바의 Actions 활성화 여부
//   enableCustomRowSelectType = "multi" || "single" || "none", // 행(row)의 선택 타입 (single, multi, none)
//   enableCustomGlobalFilter = false, // 전체 검색 활성화 여부
//   enableCustomRowActions = false, // 행(row) 액션 활성화 여부
//   enableColumnOrdering = false, // 열(columns) 이동 활성화 여부
//   enableRowOrdering = false, // 행(row) 이동 활성화 여부
//   tableWidth = "100%",
//   tableHeight = "100%",
//   tableConainerSX = {},
//   enableCustomFilter = false,
//   filterOptions = null,
//   onFilterChange = null,
//   templateSX = {},
// }) {
//   const theme = useTheme(); // MUI 테마 사용
//   const [rowSelection, setRowSelection] = useState([]); // 선택된 행(row) 데이터를 상태로 관리
//   const [isDelete, setIsDelete] = useState(false); // 삭제 시 리렌더링을 위한 상태

//   const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
//   const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태

//   // 버튼 렌더링
//   function renderToolBarButtons(table) {
//     // 사용자 정의 버튼
//     if (toolBarCustomButtons) {
//       return toolBarCustomButtons.map((buttons, index) => (
//         <div key={index}>
//           {buttons.type === 'add' && (
//             <Buttons.Add
//               onClick={() => table.setCreatingRow(true)} // 행 생성 다이얼로그 열기
//               sx={{ padding: '12px 16px' }}
//             />
//           )}
//           {buttons.type === 'delete' && (
//             <Buttons.Delete
//               onClick={() => onDeleteRows(table, setRowSelection, setIsDelete)} // 외부에서 삭제 로직 실행
//               disabled={table.getSelectedRowModel().rows.length === 0}
//               sx={{ padding: '12px 16px' }}
//             />
//           )}
//           {(buttons.type !== 'add' && buttons.type !== 'delete') && (
//             <Buttons.Custom
//               startIcon={buttons.Icon && buttons.Icon()}
//               onClick={() => {
//                 if(buttons.onClick) buttons.onClick(table);
//               }}
//               color = {buttons.color}
//               disabled={buttons.type === "delete" && table.getSelectedRowModel().rows.length === 0}
//               sx={{ padding: '12px 16px' }}
//             >
//               {buttons.text}
//             </Buttons.Custom>
//           )}
//         </div>
//       ));
//     }
//     // 기본 버튼
//     return (
//       <>
//         <Buttons.Add
//           onClick={() => table.setCreatingRow(true)} // 행 생성 다이얼로그 열기
//           sx={{ padding: '12px 16px' }}
//         />
//         <Buttons.Delete
//           onClick={() => onDeleteRows(table, setRowSelection, setIsDelete)} // 외부에서 삭제 로직 실행
//           disabled={table.getSelectedRowModel().rows.length === 0}
//           sx={{ padding: '12px 16px' }}
//         />
//       </>
//     );
//   }

//   // 테이블 렌더링
//   return (
//     <Box
//       sx={{
//         width: tableWidth,
//         height: tableHeight,
//         display: "flex",
//         flexDirection: "column",
//         ...templateSX,
//       }}
//     >
//       <MaterialReactTable
//         //////////////////////////////////
//         // 테이블에 표시할 데이터
//         //////////////////////////////////

//         columns={columns} // 칼럼 정의 데이터
//         data={data} // 테이블에 표시할 데이터
//         //////////////////////////////////
//         // Internal 테이블 옵션
//         //////////////////////////////////

//         enableSorting={false} // 정렬 기능
//         enableColumnFilters={false} //  컬럼별 필터 기능
//         enablePagination={false} // 페이지네이션 기능
//         enableRowSelection={false} // 행 선택 기능
//         enableMultiRowSelection={false} // 다중 행 선택 기능
//         enableGlobalFilter={enableCustomGlobalFilter} // 전체 검색 기능
//         enableDensityToggle={false} // 컨테이너 밀도 조절 기능
//         enableHiding={false} // 컬럼 숨김 기능
//         enableFullScreenToggle={false} // 전체 화면 기능
//         enableColumnActions={false} // 컬럼 액션 기능
//         enableEditing={enableEditing} // 테이블 각 행 편집 기능
//         enableStickyHeader={true} // 테이블 헤더 고정 기능
//         createDisplayMode={createDisplayMode} // 생성 모드 표시 설정
//         editDisplayMode={editDisplayMode} // 수정 모드 표시 설정
//         localization={MRT_Localization_KO} // 테이블 UI 언어 설정
//         //enableSelectAll = {false} // 테이블의 행 전체 선택 기능
//         enableTopToolbar={enableTopToolbar} // 테이블 상단 툴바 활성화화 여부
//         enableBottomToolbar={enableBottomToolbar} // 테이블 하단 툴바 활성화화 여부
//         //enableRowCreation = {true} // 테이블의 행 생성 기능
//         //onCreatingRowSave = {null} // 행 생성 시 저장 이벤트 (기본으로 제공하는 생성 다이얼로그의 생성 버튼 누를 시 자동 호출, 커스텀으로 만들경우 수동으로 호출)
//         //onEditingRowSave = {null} // 행 편집 시 저장 이벤트 (기본으로 제공하는 수정 다이얼로그의 생성 버튼 누를 시 자동 호출, 커스텀으로 만들경우 수동으로 호출)
//         enableColumnOrdering={enableColumnOrdering} // columns 이동 기능
//         enableRowOrdering={enableRowOrdering} // row 이동 기능

//         // 테이블의 현재 상태(예: 선택된 행, 정렬 상태, 필터 상태 등)를 저장하고 관리하는 객체
//         state={{
//           globalFilter,
//           rowSelection: enableCustomRowSelectType === "multi" ? rowSelection : {}
//         }}
//         onGlobalFilterChange={setGlobalFilter}
//         onRowSelectionChange={setRowSelection}

//         //////////////////////////////////

//         // tbody에 스타일 적용
//         muiTableBodyProps={{
//           sx: {
//             // 데이터가 없을 때 배경색 변경
//             backgroundColor: data.length === 0 ? theme.palette.background.tableEmptyBg : theme.palette.background.paper,
//           },
//         }}
//         // 테이블의 각 행(Row)에 추가적인 스타일 및 이벤트 적용
//         muiTableBodyRowProps={({ row }) => {
//           if (enableCustomRowSelectType === "multi") {
//             return {
//               onClick: () => {
//                 setRowSelection((prev) => ({
//                   ...prev,
//                   [row.id]: !prev[row.id],
//                 }));
//                 onSelectedRows?.(row);
//               },
//               selected: rowSelection[row.id],
//               sx: {
//                 cursor: "pointer",
//                 height: "50px",
//                 "& td": {
//                   backgroundColor: rowSelection[row.id] ? theme.palette.primary.lighterOpacity : theme.palette.background.paper,
//                 },
//                 "&:hover": {
//                   height: "50px",
//                   backgroundColor: `${theme.palette.action.hover} !important`,
//                   // "& td": {
//                   // backgroundColor: theme.palette.action.hover,
//                   // },
//                 },
//               },
//             };
//           } else if (enableCustomRowSelectType === "single") {
//             return {
//               onClick: () => {
//                 const exist = rowSelection.some((rowId) => rowId === row.id);
//                 if (exist) {
//                   setRowSelection([]);
//                 } else {
//                   setRowSelection([row.id]);
//                 }
//                 onSelectedRows?.(row);
//               },
//               selected: rowSelection[0] === row.id,
//               sx: {
//                 cursor: "pointer",
//                 height: "50px",
//                 "& td": {
//                   backgroundColor: rowSelection[0] === row.id ? theme.palette.primary.lighterOpacity : theme.palette.background.paper,
//                 },
//                 "&:hover": {
//                   height: "50px",
//                   backgroundColor: `${theme.palette.action.hover} !important`,
//                   // "& td": { backgroundColor: theme.palette.action.hover },
//                 },
//               },
//             };
//           } else {
//             return {
//               // none
//               sx: {
//                 //cursor: "pointer",
//                 height: "50px",
//                 "& td": {
//                   backgroundColor: theme.palette.background.paper,
//                 },
//                 "&:hover": {
//                   height: "50px",
//                   backgroundColor: `${theme.palette.action.hover} !important`,
//                   // "& td": {
//                   // backgroundColor: theme.palette.action.hover,
//                   // },
//                 },
//               },
//             };
//           }
//         }}

//         // 상단 툴바 내부 액션 렌더링 (내부 기능 사용, 오른쪽에 배치 됨.)
//         renderToolbarInternalActions={({ table }) => (
//           <Box
//             sx={{
//               width: '100%',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'space-between', // 왼쪽 검색창, 오른쪽 버튼 분리
//               gap: 2,
//             }}
//           >
//             {/* 전체 검색창 */}
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//               <IconButton onClick={() => setShowSearch((prev) => !prev)}>
//                 <SearchIcon />
//               </IconButton>

//               <Collapse in={showSearch} orientation="horizontal">
//                 <TextField
//                   size="small"
//                   placeholder="전체 검색"
//                   value={globalFilter}
//                   onChange={(e) => {
//                     setGlobalFilter(e.target.value);
//                     table.setGlobalFilter(e.target.value);
//                   }}
//                   sx={{ minWidth: 200 }}
//                   variant="outlined"
//                   slots={{
//                     input: OutlinedInput,
//                   }}
//                   slotProps={{
//                     input: {
//                       endAdornment: (
//                         <InputAdornment position="end">
//                         <IconButton
//                           onClick={() => {
//                             setGlobalFilter('');
//                             table.setGlobalFilter('');
//                             setShowSearch(false); // 닫기
//                           }}
//                         >
//                           <CloseIcon fontSize="small" />
//                         </IconButton>
//                       </InputAdornment>
//                       ),
//                     }
//                   }}
//                 />
//               </Collapse>
//             </Box>

//             {/* 버튼들 */}
//             <Box sx={{ display: 'flex', gap: 2 }}>
//               {enableCustomTopToolbarActions && renderToolBarButtons(table)}
//             </Box>
//           </Box>
//         )}

//         // 상단 툴바 커스텀 액션 렌더링 (내부 기능들 이외에 사용자 정의 추가, 왼쪽에 배치 됨.)
//         renderTopToolbarCustomActions={({ table }) => {
//           return (
//             <Box>
//               {/* 필터 활성화시 */}
//               {enableCustomFilter && filterOptions && (
//                 <Box
//                   sx={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: "16px",
//                     padding: "8px 0px 0px 16px",
//                   }}
//                 >
//                   {/* 평가연도 필터 */}
//                   {filterOptions.yearList && (
//                     <FormControl size="small">
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "16px",
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             //fontFamily: FONT_PROPS.PRIMARY,
//                             fontWeight: FONT_PROPS.SEMIBOLD,
//                             fontSize: 15,
//                             color: theme.text,
//                           }}
//                         >
//                           평가연도
//                         </Typography>
//                         <Select
//                           value={filterOptions.currentFilters.year || ""}
//                           displayEmpty
//                           onChange={(e) => {
//                             if (onFilterChange) {
//                               onFilterChange({
//                                 ...filterOptions.currentFilters,
//                                 year: e.target.value,
//                               });
//                             }
//                           }}
//                           sx={{
//                             width: "100px",
//                             //fontFamily: FONT_PROPS.PRIMARY,
//                             fontWeight: FONT_PROPS.SEMIBOLD,
//                             color: theme.text,
//                             fontSize: 15,
//                           }}
//                         >
//                           {/* reverse가 문제 생길 시 filterOptions.yearList를 복사한 뒤 reverse 사용 할 것 */}
//                           {filterOptions.yearList.reverse().map((year, index) => (
//                             <MenuItem
//                               key={index}
//                               value={year}
//                               sx={{
//                                 //fontFamily: FONT_PROPS.PRIMARY,
//                                 fontWeight: FONT_PROPS.SEMIBOLD,
//                                 color: theme.text,
//                                 fontSize: 15,
//                               }}
//                             >
//                               {year}년
//                             </MenuItem>
//                           ))}
//                         </Select>
//                       </Box>
//                     </FormControl>
//                   )}

//                   {/* 평가월 필터 */}
//                   {filterOptions.monthList && (
//                     <FormControl size="small">
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "16px",
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             //fontFamily: FONT_PROPS.PRIMARY,
//                             fontWeight: FONT_PROPS.SEMIBOLD,
//                             fontSize: 15,
//                             color: theme.text,
//                           }}
//                         >
//                           평가월
//                         </Typography>
//                         <Select
//                           value={filterOptions.currentFilters.month || ""}
//                           displayEmpty
//                           onChange={(e) => {
//                             if (onFilterChange) {
//                               onFilterChange({
//                                 ...filterOptions.currentFilters,
//                                 month: e.target.value,
//                               });
//                             }
//                           }}
//                           sx={{
//                             width: "80px",
//                             //fontFamily: FONT_PROPS.PRIMARY,
//                             fontWeight: FONT_PROPS.SEMIBOLD,
//                             color: theme.text,
//                             fontSize: 15,
//                           }}
//                         >
//                           {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].reverse().map((month, index) => (
//                             <MenuItem
//                               key={index}
//                               value={month}
//                               sx={{
//                                 //fontFamily: FONT_PROPS.PRIMARY,
//                                 fontWeight: FONT_PROPS.SEMIBOLD,
//                                 color: theme.text,
//                                 fontSize: 15,
//                               }}
//                             >
//                               {month}월
//                             </MenuItem>
//                           ))}
//                         </Select>
//                       </Box>
//                     </FormControl>
//                   )}

//                   {/* 설비명 필터 */}
//                   {filterOptions.machineList && (
//                     <FormControl size="small">
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "16px",
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             //fontFamily: FONT_PROPS.PRIMARY,
//                             fontWeight: FONT_PROPS.SEMIBOLD,
//                             fontSize: 15,
//                             color: theme.text,
//                           }}
//                         >
//                           설비명
//                         </Typography>
//                         <Select
//                           value={filterOptions.currentFilters.machine_id || ""}
//                           displayEmpty
//                           onChange={(e) => {
//                             if (onFilterChange) {
//                               const fieldName = filterOptions.currentFilters.hasOwnProperty("machine_id") ? "machine_id" : "";

//                               onFilterChange({
//                                 ...filterOptions.currentFilters,
//                                 [fieldName]: e.target.value,
//                               });
//                             }
//                           }}
//                           sx={{
//                             minWidth: "120px",
//                             //fontFamily: FONT_PROPS.PRIMARY,
//                             fontWeight: FONT_PROPS.SEMIBOLD,
//                             color: FONT_PROPS.DARK,
//                             fontSize: 15,
//                           }}
//                         >
//                           {filterOptions.machineList.map((data, index) => {
//                             const idField = data.hasOwnProperty("machine_id") ? "machine_id" : "";

//                             return (
//                               <MenuItem
//                                 key={index}
//                                 value={data[idField]}
//                                 sx={{
//                                   //fontFamily: FONT_PROPS.PRIMARY,
//                                   fontWeight: FONT_PROPS.SEMIBOLD,
//                                   color: theme.text,
//                                   fontSize: 15,
//                                 }}
//                               >
//                                 {data.machine_name}
//                               </MenuItem>
//                             );
//                           })}
//                         </Select>
//                       </Box>
//                     </FormControl>
//                   )}
//                 </Box>
//               )}
//               {/* 버튼 활성화시 */}
//               {/* <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: 'flex-end',
//                   gap: "8px"
//                 }}>
//                 {enableCustomTopToolbarActions && renderToolBarButtons(table)}
//               </Box> */}
//             </Box>
//           );
//         }}
//         // 테이블의 각 행(Row)의 사용자 정의 액션 버튼 추가 (수정, 삭제 등)
//         renderRowActions={
//           enableCustomRowActions
//             ? ({ row }) => {
//                 return <ButtonRowActions row={row} />; // 사용자 정의 액션 버튼 반환
//               }
//             : null
//         }
//         // 사용자 정의 액션 버튼 위치 설정 (첫번째 열 또는 마지막 열)
//         positionActionsColumn="last"
//         // 행(Row)을 생성할 때 나타나는 다이얼로그(Dialog) 창의 내용을 커스터마이징하는 옵션
//         renderCreateRowDialogContent={({ table }) => {
//           return <CreateRowModal table={table} />; // 사용자 정의 생성 모달 반환
//         }}
//         // 행(Row)을 편집할 때 나타나는 다이얼로그(Dialog) 창의 내용을 커스터마이징하는 옵션
//         renderEditRowDialogContent={({ row, table }) => {
//           return <EditRowModal row={row} table={table} />; // 사용자 정의 편집 모달 반환
//         }}
//         //onEditingRowChage

//         // 행(Row) 이동 옵션
//         muiRowDragHandleProps={({ table }) => ({
//           onDragEnd: () => {
//             const { draggingRow, hoveredRow } = table.getState();
//             if (hoveredRow && draggingRow) {
//               const allRows = table.getRowModel().rows;

//               const draggedRowData = allRows[draggingRow.index]?.original;
//               const hoveredRowData = allRows[hoveredRow.index]?.original;
//               // console.log('드래그한 row의 데이터:', draggedRowData);
//               // console.log('호버된 row의 데이터:', hoveredRowData);

//               onRowOrdering(draggedRowData, hoveredRowData);
//               // tempData.splice(
//               //   hoveredRow.index,
//               //   0,
//               //   tempData.splice(draggingRow.index, 1)[0]
//               // );
//               // setData([...tempData]); // 순서 업데이트
//             }
//           },
//         })}

//         //////////////////////////////////
//         // Internal 테이블 스타일
//         //////////////////////////////////

//         // 테이블을 감싼 컨테이너 스타일
//         muiTableContainerProps={{
//           sx: {
//             flex: 1,
//             height: "100%",
//             overflowY: "auto",
//             backgroundColor: theme.palette.background.paper,
//             ...tableConainerSX, // 사용자 정의 컨테이너 스타일
//           },
//         }}
//         // 테이블을 감싼 페이퍼 스타일
//         muiTablePaperProps={{
//           sx: {
//             boxShadow: "none",
//             height: "100%",
//             display: "flex",
//             flexDirection: "column",
//           },
//         }}
//         // 테이블 상단 툴바 스타일
//         muiTopToolbarProps={{
//           sx: {
//           //   alignItems: "center",
//             backgroundColor: theme.palette.background.paper,
//             borderBottom: `1px solid ${theme.palette.divider}`,
//           //   p: "10px",
//           //   transition: "none", // transition 제거
//           //   WebkitTransition: "none", // webkit-transition 제거
//             "& > div:nth-of-type(2)": {
//               alignItems: "center",
//               // padding: "0 !important",
//             },
//           },
//         }}
//         // 테이블 하단 툴바 스타일
//         muiBottomToolbarProps={{
//           sx: {
//             backgroundColor: theme.palette.background.paper,
//             transition: "none", // transition 제거
//             WebkitTransition: "none", // webkit-transition 제거
//             // 하단 툴바의 특정 부분 숨기기
//             "& > .MuiBox-root:nth-of-type(2)": {
//               display: "none", // 두 번째 Box 요소 숨기기
//             },
//             // 또는 더 구체적으로 지정 (이 부분을 사용하면 페이지 네이션을 사용할 수 없음.)
//             // "& > .css-1llu0od, & > div:has(> span + .MuiBox-root)": {
//             //   display: "none",
//             // },
//           },
//         }}
//         // 테이블 스타일
//         // muiTableProps={{
//         //     sx: {
//         //         border: '1px solid #ddd',
//         //         "& *": {
//         //             fontFamily: `${FONT_PROPS.PRIMARY}`,
//         //             color: `${FONT_PROPS.DARK}`,
//         //         },
//         //     }
//         // }}

//         // 테이블 안 헤더의 전체 셀 스타일 (배경색, 높이 조정, 클릭 이벤트, 마우스 오버 효과 등)
//         muiTableHeadRowProps={{
//           sx: {
//             backgroundColor: theme.palette.background.paper,
//           },
//         }}
//         // 테이블 안 헤더의 개별 셀 스타일 (정렬, 패딩, 텍스트색상, 툴팁, 이벤트 등)
//         muiTableHeadCellProps={{
//           sx: {
//             fontFamily: `${FONT_PROPS.PRIMARY}`,
//             color: `${FONT_PROPS.DARK}`,
//             fontWeight: `${FONT_PROPS.SEMIBOLD}`,
//             fontSize: 15,
//           },
//         }}
//         // 테이블 안 바디의 개별 셀 스타일 (정렬, 패딩, 텍스트색상, 툴팁, 이벤트 등)
//         muiTableBodyCellProps={{
//           sx: {
//             color: theme.palette.text.primary,
//             fontWeight: 600, // semi bold
//             fontSize: 14,
//           },
//         }}
//         // 툴바(Top Toolbar) 상단 또는 하단에 경고 배너(Alert Banner) 스타일
//         muiToolbarAlertBannerProps={{
//           onClick: () => {
//             setRowSelection([]); // 선택된 행 초기화
//             onSelectedRows?.(null); // 선택된 행 초기화
//           },
//           sx: {
//             bgcolor: theme.palette.background.paper,
//             color: theme.palette.text.primary,
//             "& .MuiAlert-message": {
//               color: theme.palette.text.primary,
//               fontFamily: theme.typography.fontFamily,
//               fontSize: "0.875rem",
//             },
//             "& .MuiAlert-icon": {
//               color: theme.palette.text.primary,
//             },
//             "& .MuiAlert-action": {
//               color: theme.palette.text.primary,
//             },
//             padding: "8px 16px",
//             margin: 0,
//             borderTop: `1px solid ${theme.palette.divider}`,
//           },
//         }}
//         // 경고 배너(Alert Banner) 위치 설정 (상단, 하단, 숨기기)
//         positionToolbarAlertBanner="bottom"
//         //////////////////////////////////
//         // 사용자 정의(Custom) 테이블 옵션
//         //////////////////////////////////

//         // 테이블의 각 행(Row) 편집 시 이벤트 (table.options?.테이블옵션 < 이렇게 사용합니다.)
//         onCustomEditRow={({ values, row, table }) => {
//           if (editDisplayMode === "row") {
//             onEditRow?.(values, row, table); // 외부에서 편집 로직 실행
//             //console.log("✅ 편집 호출 완료!", values);
//             //table.setEditingRow(null); // 에디터 모드 종료
//           }
//         }}

//         //////////////////////////////////
//         // 사용자 정의(Custom) 테이블 스타일
//         //////////////////////////////////

//         // TODO:
//       />
//     </Box>
//   );
// }

// /////////////////////////////////////////////

// // shift, ctrl, 마우스 우클릭 예제 - 참고용도로 남겨둠
// export function TableTemplateTestExample({
//   columns = [],
//   data = [],
// }) {

//   // 키보드와 마우스로 사용하는 Selection 기능
//   const [selectedRowIds, setSelectedRowIds] = useState({});
//   const [lastClickedIndex, setLastClickedIndex] = useState(null);
//   const handleRowClick = ({ event, row }) => {
//     const index = row.index;
//     const rowId = row.id;
//     const allRows = table.getRowModel().rows;

//     if (event.shiftKey && lastClickedIndex !== null) {
//       // 🔶 Shift 범위 선택
//       const start = Math.min(lastClickedIndex, index);
//       const end = Math.max(lastClickedIndex, index);
//       const newSelected = {};
//       for (let i = start; i <= end; i++) {
//         newSelected[allRows[i].id] = true;
//       }
//       setSelectedRowIds(newSelected);
//     } else if (event.ctrlKey || event.metaKey) {
//       // 🔷 Ctrl (또는 Mac의 Cmd) → 개별 토글
//       setSelectedRowIds((prev) => ({
//         ...prev,
//         [rowId]: !prev[rowId],
//       }));
//       setLastClickedIndex(index);
//     } else {
//       // ✅ 그냥 클릭 → 해당 row만 단일 선택
//       setSelectedRowIds({ [rowId]: true });
//       setLastClickedIndex(index);
//     }
//   };

//   //////////////////////////////
//   // MRT의 table 인스턴싱 (테이블 상태와 동작을 제어가 필요하여 사용중입니다.)
//   //////////////////////////////
//   const table = useMaterialReactTable({
//     columns,
//     data,
//     enableRowSelection: true,
//     getRowId: (row) => row.id, // row마다 고유 id 필요!
//     state: {
//       rowSelection: selectedRowIds, // 외부에서 상태 제어
//     },
//     muiTableBodyRowProps: ({ row }) => ({
//       onClick: (event) => handleRowClick({ event, row }),
//     }),
//   });

//   // MRT 렌더링
//   return (
//     <MaterialReactTable table={table} />
//   );
// }
